import { AVAILABILITIES, LIMITS } from '@sbg/shared/constants';
import type { Availability } from '@sbg/shared/constants';
import type { CatalogFilterState } from './api';

/*
 * The shop's filters in the address bar, read and written WITHOUT Zod.
 *
 * The rules are the API's `catalogQuerySchema` (packages/shared/src/schemas/catalog.ts). Using that schema here
 * would put Zod (25 kB gzip) in the first load of the shop page and everything that imports this module, for the
 * sake of reading a query string — so the rules are repeated below, and `url-state.parity.test.ts` runs this
 * implementation and the schema side by side on hand-written and random queries: if the schema changes, that test
 * fails until this file agrees.
 */

/** `CATALOG_SORTS` of the schema. */
const SORTS = ['featured', 'newest', 'price_asc', 'price_desc', 'relevance'] as const;
/** `slugSchema`. */
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** `listOf(...).max(20)`. */
const MAX_LIST_ITEMS = 20;
const MAX_PRICE = 100_000_000;

/** `?a=1&a=2&b=3` → `{ a: ['1','2'], b: '3' }`: one value stays a string, several become a list. */
function searchParamsToObject(params: URLSearchParams): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  for (const key of new Set(params.keys())) {
    const values = params.getAll(key);
    out[key] = values.length === 1 ? (values[0] ?? '') : values;
  }
  return out;
}

type Raw = string | string[];

/** `slugSchema`: trimmed, 1–120 characters of lower-case words joined by hyphens. */
const slug = (value: Raw): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length >= 1 && trimmed.length <= LIMITS.slug.max && SLUG.test(trimmed)
    ? trimmed
    : null;
};

/** `z.coerce.number().int().min(min).max(max)`: the value is coerced with `Number()`, as Zod does. */
const whole = (value: unknown, min: number, max: number): number | null => {
  const number = Number(value);
  return Number.isInteger(number) && number >= min && number <= max ? number : null;
};

/**
 * `listOf(item)`: `?x=a,b` and `?x=a&x=b` both mean two values; a single string drops empty parts, repeated keys
 * keep them (and an empty part is then an invalid item). At most 20 items; one bad item voids the list.
 */
function list<T>(value: Raw, item: (part: string) => T | null): T[] | null {
  const parts =
    typeof value === 'string'
      ? value
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean)
      : value.flatMap((entry) => entry.split(',').map((part) => part.trim()));
  if (parts.length > MAX_LIST_ITEMS) return null;
  const items: T[] = [];
  for (const part of parts) {
    const parsed = item(part);
    if (parsed === null) return null;
    items.push(parsed);
  }
  return items;
}

const availabilityOf = (part: string): Availability | null =>
  (AVAILABILITIES as readonly string[]).includes(part) ? (part as Availability) : null;

const DEFAULTS: CatalogFilterState = { sort: 'featured' };

/**
 * The shop's filter state from its URL. The URL is user-editable, so this never throws: a value the
 * rules reject (`?priceMin=abc`, an unknown availability …) is dropped and the rest is kept.
 * `relevance` only makes sense with a search text, so without one it becomes the default sort.
 */
export function parseCatalogSearch(params: URLSearchParams): CatalogFilterState {
  const raw = searchParamsToObject(params);
  const filters: CatalogFilterState = { ...DEFAULTS };

  const q = raw['q'];
  if (typeof q === 'string') {
    const text = q.trim();
    if (text.length <= LIMITS.search.max && text !== '') filters.q = text;
  }
  const category = raw['category'] === undefined ? null : slug(raw['category']);
  if (category !== null) filters.category = category;

  for (const key of ['color', 'finish', 'origin', 'application'] as const) {
    const value = raw[key];
    const parsed = value === undefined ? null : list(value, (part) => slug(part));
    if (parsed !== null) filters[key] = parsed;
  }
  const thickness =
    raw['thickness'] === undefined ? null : list(raw['thickness'], (part) => whole(part, 1, 200));
  if (thickness !== null) filters.thickness = thickness;
  const availability =
    raw['availability'] === undefined ? null : list(raw['availability'], availabilityOf);
  if (availability !== null) filters.availability = availability;

  for (const key of ['priceMin', 'priceMax'] as const) {
    const value = raw[key];
    const parsed = value === undefined ? null : whole(value, 0, MAX_PRICE);
    if (parsed !== null) filters[key] = parsed;
  }
  const featured = raw['featured'];
  if (featured === 'true' || featured === 'false') filters.featured = featured === 'true';

  const sort = raw['sort'];
  if (typeof sort === 'string' && (SORTS as readonly string[]).includes(sort)) {
    filters.sort = sort as CatalogFilterState['sort'];
  }
  if (filters.sort === 'relevance' && !filters.q) filters.sort = 'featured';
  return filters;
}

/** The URL query string for a filter state (defaults and empty values omitted, stable order): `catalogQueryToSearchParams`. */
export function toCatalogSearch(filters: Partial<CatalogFilterState>): string {
  const params = new URLSearchParams();
  const put = (
    key: string,
    value: string | number | boolean | readonly (string | number)[] | undefined,
  ): void => {
    if (value === undefined || value === '') return;
    if (typeof value === 'object') {
      if (value.length > 0) params.set(key, value.map(String).join(','));
      return;
    }
    params.set(key, String(value));
  };
  put('q', filters.q);
  put('category', filters.category);
  put('color', filters.color);
  put('finish', filters.finish);
  put('origin', filters.origin);
  put('application', filters.application);
  put('thickness', filters.thickness);
  put('availability', filters.availability);
  put('priceMin', filters.priceMin);
  put('priceMax', filters.priceMax);
  if (filters.sort && filters.sort !== 'featured') put('sort', filters.sort);
  put('featured', filters.featured);
  return params.toString();
}

export type ListFilterKey = 'color' | 'finish' | 'origin' | 'application' | 'availability';

/** Toggle one value of a list filter (`color`, `finish` …) — immutable, empty lists are removed. */
export function toggleFilterValue(
  filters: CatalogFilterState,
  key: ListFilterKey,
  value: string,
): CatalogFilterState {
  const current: readonly string[] = filters[key] ?? [];
  const next = current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
  const { [key]: _removed, ...rest } = filters;
  return next.length > 0 ? { ...rest, [key]: next } : rest;
}

/** Toggle one thickness (mm). */
export function toggleThickness(filters: CatalogFilterState, mm: number): CatalogFilterState {
  const current = filters.thickness ?? [];
  const next = current.includes(mm) ? current.filter((item) => item !== mm) : [...current, mm];
  const { thickness: _removed, ...rest } = filters;
  return next.length > 0 ? { ...rest, thickness: next } : rest;
}

const COUNTED_LISTS = [
  'color',
  'finish',
  'origin',
  'application',
  'thickness',
  'availability',
] as const;

/** How many separate filters are active (the sort order and the search text are not filters). */
export function countActiveFilters(filters: CatalogFilterState): number {
  let count = 0;
  if (filters.category) count += 1;
  for (const key of COUNTED_LISTS) count += filters[key]?.length ?? 0;
  if (filters.priceMin !== undefined || filters.priceMax !== undefined) count += 1;
  if (filters.featured !== undefined) count += 1;
  return count;
}

/** Everything but the search text and the sort: what "Clear all" resets to. */
export function clearFilters(filters: CatalogFilterState): CatalogFilterState {
  return { sort: filters.sort, ...(filters.q ? { q: filters.q } : {}) };
}
